import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewCatalogueLink } from '@/components/article';
import { NavigationPill } from '@/components/home';
import { PageHeader, PageShell } from '@/components/layout';
import { RevealOnView } from '@/components/ui';
import { CONTENT_SLIDE, HEADER_FADE, REVEAL_TIMING, useInView } from '@/utils/animation';
import { OurStoryRow } from '@/pages/OurStory/components';
import { ContactBlock, ContactForm } from './components';
import { CONTACT_REGIONS, GENERAL_ENQUIRIES_SECTION } from './content';

const BEAT_MS = 100;
const INTRO_DELAY_MS = Math.round(HEADER_FADE.durationMs * 0.25);
const REST_DELAY_MS = INTRO_DELAY_MS + Math.round(REVEAL_TIMING.durationMs * 0.3);

const headerFadeStyle = (visible: boolean) => ({
  opacity: visible ? 1 : 0,
  transition: `opacity ${HEADER_FADE.durationMs}ms ${HEADER_FADE.ease}`,
});

const REGION_KEY_BY_ID: Record<string, 'asia' | 'europe' | 'americas'> = {
  'contact-asia-heading': 'asia',
  'contact-europe-heading': 'europe',
  'contact-americas-heading': 'americas',
};

export default function Contact() {
  const { t } = useTranslation();
  const viewCatalogueLink = useViewCatalogueLink();
  const [asia, europe, americas] = CONTACT_REGIONS;
  const introParagraphs = t('contact.header.intro', { returnObjects: true }) as string[];

  const [section1Visible, setSection1Visible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSection1Visible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, []);

  const asiaRef = useRef<HTMLElement>(null);
  const asiaInView = useInView(asiaRef, { threshold: 0 });
  const [asiaVisible, setAsiaVisible] = useState(false);
  useEffect(() => {
    if (!asiaInView) return;
    const timer = setTimeout(() => setAsiaVisible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, [asiaInView]);

  const europeRef = useRef<HTMLElement>(null);
  const europeInView = useInView(europeRef, { threshold: 0 });
  const [europeVisible, setEuropeVisible] = useState(false);
  useEffect(() => {
    if (!europeInView) return;
    const timer = setTimeout(() => setEuropeVisible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, [europeInView]);

  const americasRef = useRef<HTMLElement>(null);
  const americasInView = useInView(americasRef, { threshold: 0 });
  const [americasVisible, setAmericasVisible] = useState(false);
  useEffect(() => {
    if (!americasInView) return;
    const timer = setTimeout(() => setAmericasVisible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, [americasInView]);

  const generalRef = useRef<HTMLElement>(null);
  const generalInView = useInView(generalRef, { threshold: 0 });
  const [generalVisible, setGeneralVisible] = useState(false);
  useEffect(() => {
    if (!generalInView) return;
    const timer = setTimeout(() => setGeneralVisible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, [generalInView]);

  const regionLabel = (id: string) => {
    const key = REGION_KEY_BY_ID[id];
    return key ? t(`contact.regions.${key}`) : id;
  };

  return (
    <PageShell
      title={t('contact.meta.title')}
      description={t('contact.meta.description')}
      path="/contact"
      hideHeader
    >
      <NavigationPill />
      <PageHeader title={t('contact.pageHeader')} links={viewCatalogueLink} />

      <section
        aria-labelledby="contact-us-heading"
        className="bg-clearlab-oat px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col gap-10 md:flex-row md:items-start md:justify-between">
          <h2
            id="contact-us-heading"
            style={headerFadeStyle(section1Visible)}
            className="text-[32px] font-normal leading-8 tracking-[-0.025em] text-clearlab-dusk md:min-w-[240px] md:max-w-[440px] md:flex-1 xl:w-[440px] xl:flex-none"
          >
            {t('contact.header.title')}
          </h2>
          <div className="flex flex-col items-start gap-20 md:w-[600px]">
            <RevealOnView
              {...CONTENT_SLIDE}
              inView={section1Visible}
              delayMs={INTRO_DELAY_MS}
              className="flex w-full flex-col"
            >
              {introParagraphs.map((paragraph, idx) => (
                <p
                  key={idx}
                  className={`w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight ${
                    idx < introParagraphs.length - 1 ? 'mb-10' : ''
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </RevealOnView>
            <RevealOnView
              {...CONTENT_SLIDE}
              inView={section1Visible}
              delayMs={REST_DELAY_MS}
              className="w-full"
            >
              <ContactForm />
            </RevealOnView>
          </div>
        </div>
      </section>

      <section
        ref={asiaRef}
        aria-label={regionLabel(asia.id)}
        className="bg-clearlab-divider-light/20 px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col">
          <div id={asia.id} className="scroll-mt-[280px]">
            <OurStoryRow title={regionLabel(asia.id)} visible={asiaVisible}>
              <RevealOnView
                {...CONTENT_SLIDE}
                inView={asiaVisible}
                delayMs={INTRO_DELAY_MS}
                className="flex w-full flex-col items-start gap-20"
              >
                {asia.contacts.map((contact) => (
                  <ContactBlock key={contact.name} contact={contact} />
                ))}
              </RevealOnView>
            </OurStoryRow>
          </div>
        </div>
      </section>

      <section
        ref={europeRef}
        aria-label={regionLabel(europe.id)}
        className="bg-clearlab-oat px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col">
          <div id={europe.id} className="scroll-mt-[280px]">
            <OurStoryRow title={regionLabel(europe.id)} visible={europeVisible}>
              <RevealOnView
                {...CONTENT_SLIDE}
                inView={europeVisible}
                delayMs={INTRO_DELAY_MS}
                className="flex w-full flex-col items-start gap-20"
              >
                {europe.contacts.map((contact) => (
                  <ContactBlock key={contact.name} contact={contact} />
                ))}
              </RevealOnView>
            </OurStoryRow>
          </div>
        </div>
      </section>

      <section
        ref={americasRef}
        aria-label={regionLabel(americas.id)}
        className="bg-clearlab-divider-light/20 px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col">
          <div id={americas.id} className="scroll-mt-[280px]">
            <OurStoryRow title={regionLabel(americas.id)} visible={americasVisible}>
              <RevealOnView
                {...CONTENT_SLIDE}
                inView={americasVisible}
                delayMs={INTRO_DELAY_MS}
                className="flex w-full flex-col items-start gap-20"
              >
                {americas.contacts.map((contact) => (
                  <ContactBlock key={contact.name} contact={contact} />
                ))}
              </RevealOnView>
            </OurStoryRow>
          </div>
        </div>
      </section>

      <section
        ref={generalRef}
        aria-label={t('contact.general.title')}
        className="bg-clearlab-oat px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col gap-30">
          <OurStoryRow title={t('contact.general.title')} visible={generalVisible}>
            <RevealOnView
              {...CONTENT_SLIDE}
              inView={generalVisible}
              delayMs={INTRO_DELAY_MS}
              className="flex w-full flex-col items-start gap-10"
            >
              <p className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                {t('contact.general.intro')}
              </p>
              <ContactBlock contact={GENERAL_ENQUIRIES_SECTION.contact} />
            </RevealOnView>
          </OurStoryRow>
          <RevealOnView
            {...CONTENT_SLIDE}
            inView={generalVisible}
            delayMs={INTRO_DELAY_MS}
            className="flex w-full flex-col items-start gap-6"
          >
            <hr className="w-full border-0 border-t-[0.7px] border-clearlab-divider-darker" />
            <p className="w-full text-[12px] font-normal leading-[14px] text-clearlab-steel">
              {t('contact.disclaimer')}
            </p>
          </RevealOnView>
        </div>
      </section>
    </PageShell>
  );
}
